/**
 * @file manual-payment-form-utils.ts
 *
 * Pure helpers for the Manual payment form: derive the editable fields from the
 * loaded gateway (with safe defaults), and build the partial gateway patch to
 * persist on save.
 */

import type { PaymentGateway } from '../../api/payments-types';
import type { ManualPaymentFormFields } from './manual-payment-form-types';

/**
 * Derives the form fields from the manual gateway, defaulting when absent.
 *
 * @param gateway - The loaded manual gateway, or undefined.
 *
 * @returns The seed fields.
 */
export function deriveManualFields(gateway?: PaymentGateway): ManualPaymentFormFields {
  return {
    enabled: gateway?.enabled ?? false,
    title: gateway?.title ?? '',
    description: gateway?.description ?? '',
    instructions: gateway?.instructions ?? '',
    enableForLocalPickupOnly: gateway?.enableForLocalPickupOnly ?? false,
  };
}

/**
 * Builds the gateway patch from the form fields, trimming text.
 *
 * @param fields - The current form fields.
 *
 * @returns The partial gateway to PUT.
 */
export function buildManualPatch(fields: ManualPaymentFormFields): Partial<PaymentGateway> {
  return {
    enabled: fields.enabled,
    title: fields.title.trim(),
    description: fields.description.trim(),
    instructions: fields.instructions.trim(),
    enableForLocalPickupOnly: fields.enableForLocalPickupOnly,
  };
}
